import { defineStore } from 'pinia'

export type RealtimeConnectionState =
  | 'initialized'
  | 'connecting'
  | 'connected'
  | 'unavailable'
  | 'failed'
  | 'disconnected'

export const WEBSOCKET_DISCONNECTED_WARNING_DELAY_MS = 10_000

const KNOWN_STATES: readonly RealtimeConnectionState[] = [
  'initialized',
  'connecting',
  'connected',
  'unavailable',
  'failed',
  'disconnected'
]

export function normalizeRealtimeConnectionState(state: unknown): RealtimeConnectionState {
  return KNOWN_STATES.includes(state as RealtimeConnectionState)
    ? state as RealtimeConnectionState
    : 'unavailable'
}

export function isUnavailableRealtimeState(state: RealtimeConnectionState): boolean {
  return state === 'unavailable' || state === 'failed' || state === 'disconnected'
}

interface RealtimeDiagnostics {
  appKey?: string | null
  wsHost?: string | null
  transport?: string | null
}

export const useConnectionStore = defineStore('connection', () => {
  const state = ref<RealtimeConnectionState>('initialized')
  const previousState = ref<RealtimeConnectionState | null>(null)
  const browserOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
  const lastConnectedAt = ref<number | null>(null)
  const lastStateChangedAt = ref<number | null>(null)
  const lastFrameReceivedAt = ref<number | null>(null)
  const disconnectedSince = ref<number | null>(null)
  const warningReady = ref(false)
  const transitionVersion = ref(0)
  const dismissedTransitionVersion = ref<number | null>(null)

  const appKey = ref<string | null>(null)
  const wsHost = ref<string | null>(null)
  const transport = ref<string | null>(null)

  let warningTimer: ReturnType<typeof setTimeout> | null = null
  let retryConnection: (() => void) | null = null
  let browserListenersRegistered = false

  const isUnavailable = computed(() => {
    return !browserOnline.value || isUnavailableRealtimeState(state.value)
  })

  const shouldShowWarning = computed(() => {
    return isUnavailable.value &&
      warningReady.value &&
      dismissedTransitionVersion.value !== transitionVersion.value
  })

  function clearWarningTimer() {
    if (warningTimer !== null) {
      clearTimeout(warningTimer)
      warningTimer = null
    }
  }

  function scheduleWarningIfUnavailable(timestamp = Date.now()) {
    clearWarningTimer()
    warningReady.value = false

    if (!isUnavailable.value) {
      disconnectedSince.value = null
      return
    }

    disconnectedSince.value = timestamp
    warningTimer = setTimeout(() => {
      warningTimer = null
      if (isUnavailable.value && disconnectedSince.value === timestamp) {
        warningReady.value = true
      }
    }, WEBSOCKET_DISCONNECTED_WARNING_DELAY_MS)
  }

  function recordTransition(timestamp = Date.now()) {
    lastStateChangedAt.value = timestamp
    transitionVersion.value += 1
    dismissedTransitionVersion.value = null
    scheduleWarningIfUnavailable(timestamp)
  }

  function setState(current: unknown, previous?: unknown) {
    const nextState = normalizeRealtimeConnectionState(current)
    const nextPreviousState = previous === undefined
      ? state.value
      : normalizeRealtimeConnectionState(previous)

    if (state.value === nextState && previousState.value === nextPreviousState) {
      return
    }

    previousState.value = nextPreviousState
    state.value = nextState

    const timestamp = Date.now()
    if (nextState === 'connected') {
      lastConnectedAt.value = timestamp
      markFrameReceived(timestamp)
    }

    recordTransition(timestamp)
  }

  function setBrowserOnline(online: boolean) {
    if (browserOnline.value === online) {
      return
    }

    browserOnline.value = online
    recordTransition(Date.now())
  }

  function markFrameReceived(timestamp = Date.now()) {
    lastFrameReceivedAt.value = timestamp
  }

  function setDiagnostics(diagnostics: RealtimeDiagnostics) {
    if (diagnostics.appKey !== undefined) {
      appKey.value = diagnostics.appKey
    }

    if (diagnostics.wsHost !== undefined) {
      wsHost.value = diagnostics.wsHost
    }

    if (diagnostics.transport !== undefined) {
      transport.value = diagnostics.transport
    }
  }

  function registerRetry(callback: () => void) {
    retryConnection = callback
  }

  function retry() {
    dismissedTransitionVersion.value = null
    retryConnection?.()
  }

  function dismissWarning() {
    dismissedTransitionVersion.value = transitionVersion.value
  }

  function startBrowserConnectivityMonitoring() {
    if (browserListenersRegistered || typeof window === 'undefined') {
      return
    }

    browserListenersRegistered = true
    window.addEventListener('online', handleBrowserOnline)
    window.addEventListener('offline', handleBrowserOffline)
    setBrowserOnline(typeof navigator === 'undefined' ? true : navigator.onLine)
  }

  function stopBrowserConnectivityMonitoring() {
    if (!browserListenersRegistered || typeof window === 'undefined') {
      return
    }

    browserListenersRegistered = false
    window.removeEventListener('online', handleBrowserOnline)
    window.removeEventListener('offline', handleBrowserOffline)
  }

  function handleBrowserOnline() {
    setBrowserOnline(true)
  }

  function handleBrowserOffline() {
    setBrowserOnline(false)
  }

  function stopWarningTimer() {
    clearWarningTimer()
  }

  return {
    state,
    previousState,
    browserOnline,
    lastConnectedAt,
    lastStateChangedAt,
    lastFrameReceivedAt,
    disconnectedSince,
    appKey,
    wsHost,
    transport,
    isUnavailable,
    shouldShowWarning,
    setState,
    setBrowserOnline,
    markFrameReceived,
    setDiagnostics,
    registerRetry,
    retry,
    dismissWarning,
    startBrowserConnectivityMonitoring,
    stopBrowserConnectivityMonitoring,
    stopWarningTimer
  }
})
